import { Menu as BaseMenu } from "@base-ui/react/menu";
import type { IconData } from "@loot-os/icons/types";
import type { MouseEventHandler, ReactElement, ReactNode } from "react";
import { cn } from "../foundations/cn.js";
import { POPUP, POPUP_ITEM, POPUP_MOTION } from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";
import { Icon } from "./Icon.js";

export interface MenuProps {
  /** The button that opens the menu (a Button or IconButton); it gets aria-expanded. */
  trigger: ReactElement;
  /** MenuItems, MenuGroupLabels and MenuSeparators. */
  children: ReactNode;
  align?: "start" | "center" | "end";
  defaultOpen?: boolean;
  className?: string;
}

/** A list of actions or links opened from a button (`role="menu"`). */
export function Menu({ trigger, children, align = "end", defaultOpen, className }: MenuProps) {
  return (
    <BaseMenu.Root defaultOpen={defaultOpen} modal={false}>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner
          side="bottom"
          align={align}
          sideOffset={4}
          className="z-50 outline-none"
        >
          <BaseMenu.Popup className={cn(POPUP, POPUP_MOTION, "min-w-56", className)}>
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}

export interface MenuItemProps {
  children: ReactNode;
  icon?: IconData;
  /** Makes the item a link, through the app's LinkProvider or `render`. Disabled, it isn't one. */
  href?: string;
  render?: ReactElement;
  onClick?: MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  /** Red label, for an action that destroys (Log out stays neutral). */
  danger?: boolean;
}

/** One row: a link with `href`, otherwise an action. */
export function MenuItem({
  children,
  icon,
  href,
  render,
  onClick,
  disabled,
  danger,
}: MenuItemProps) {
  const className = cn(
    POPUP_ITEM,
    "cursor-pointer",
    danger && "text-status-danger data-highlighted:text-status-danger",
  );
  const linkRender = useLinkRender(render);
  const content = (
    <>
      {icon && <Icon icon={icon} className="shrink-0" />}
      {children}
    </>
  );
  if (href !== undefined && !disabled) {
    return (
      <BaseMenu.LinkItem
        href={href}
        render={linkRender}
        closeOnClick
        onClick={onClick}
        className={className}
      >
        {content}
      </BaseMenu.LinkItem>
    );
  }
  return (
    <BaseMenu.Item onClick={onClick} disabled={disabled} className={className}>
      {content}
    </BaseMenu.Item>
  );
}

/** A label above a group, e.g. the signed-in name. */
export function MenuGroupLabel({ children }: { children: ReactNode }) {
  return (
    <div className="truncate px-2.5 pt-1.5 pb-1 font-body text-body-b3 text-text-tertiary">
      {children}
    </div>
  );
}

/** A rule between groups. */
export function MenuSeparator() {
  return <BaseMenu.Separator className="mx-1 my-1 h-px bg-border-subtle" />;
}
